/**
 * FormApproval - 表單審批頁面（簡化版）
 * 
 * 當前用戶審批表單
 */

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import { useToast } from '../../context/ToastContext';
import { useFormDetail, useApproveForm } from '../../../hooks/useApi';
import DynamicFormRenderer from '../../components/DynamicFormRenderer';

const FormApproval = () => {
    const { applicationId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { t, getText } = useI18n(['common', 'approval']);
    const toast = useToast();

    const [comment, setComment] = useState('');
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);

    const { data: result, isLoading, error } = useFormDetail(applicationId);
    const approveFormMutation = useApproveForm();

    const form = result?.data;

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error || !form) {
        return (
            <div className="p-6 text-center text-red-500">
                {error?.message || t('approval:formNotFound')}
            </div>
        );
    }

    const formConfig = form.config?.form;
    const approvalFlow = form.approvalFlow || [];
    const currentStep = approvalFlow.find(s => s.status === 'current');

    // 檢查當前用戶是否是審批人
    const isApprover = currentStep?.approvers?.some(a => 
        a.userId === user?.id || a.email === user?.email
    );

    // 執行審批動作
    const handleAction = async (action) => {
        // 拒絕和退回需要填寫原因
        if ((action === 'reject' || action === 'return') && !comment.trim()) {
            setPendingAction(action);
            setShowRejectModal(true);
            return;
        }

        try {
            await approveFormMutation.mutateAsync({
                applicationId,
                action,
                comment
            });

            const messages = {
                approve: t('approval:message.approved'),
                reject: t('approval:message.rejected'),
                return: t('approval:message.returned')
            };
            toast.success(messages[action] || t('approval:message.success'));
            navigate('/forms/search');
        } catch (err) {
            toast.error(`${t('approval:message.failed')}: ${err.message}`);
        }
    };

    // 確認拒絕/退回
    const confirmReject = async () => {
        if (!comment.trim()) {
            toast.warning(t('approval:message.pleaseInputReason'));
            return;
        }
        setShowRejectModal(false);
        await handleAction(pendingAction);
    };

    return (
        <div className="max-w-6xl mx-auto p-4 lg:p-6">
            {/* 頁面標題 */}
            <div className="mb-6">
                <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                    <span>{t('approval:approvalForm')}</span>
                    <span>→</span>
                    <span className="text-gray-900">{form.applicationId}</span>
                </div>
                <h1 className="text-2xl font-bold text-gray-900">
                    {getText(formConfig?.formName) || form.formId}
                </h1>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* 主表單區域 */}
                <div className="lg:col-span-2 space-y-6">
                    {/* 申請人資訊 */}
                    <div className="bg-white rounded-lg shadow p-4">
                        <h3 className="font-medium text-gray-900 mb-3">{t('approval:applicant')}</h3>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-medium text-lg">
                                {form.applicant?.displayName?.[0]}
                            </div>
                            <div>
                                <div className="font-medium">{form.applicant?.displayName}</div>
                                <div className="text-sm text-gray-500">
                                    {form.applicant?.department} • {form.applicant?.jobTitle}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 表單內容 */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h3 className="font-medium text-gray-900 mb-4">{t('approval:formContent')}</h3>
                        {formConfig ? (
                            <DynamicFormRenderer
                                formConfig={formConfig}
                                formData={form.formData || {}}
                                readOnly={true}
                                applicationId={applicationId}
                                formId={form.formId}
                            />
                        ) : (
                            <div className="text-sm text-gray-500">
                                {t('approval:cannotLoadFormConfig')}
                            </div>
                        )}
                    </div>

                    {/* 審批操作 */}
                    {isApprover && form.status === 'pending' && (
                        <div className="bg-white rounded-lg shadow p-4">
                            <h3 className="font-medium text-gray-900 mb-3">{t('approval:approvalComment')}</h3>
                            <textarea
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                placeholder={t('approval:inputComment')}
                                className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                rows={3}
                            />
                            <div className="mt-4 flex justify-end gap-3">
                                <button
                                    onClick={() => handleAction('return')}
                                    disabled={approveFormMutation.isPending}
                                    className="px-4 py-2 border border-orange-300 text-orange-600 rounded-lg hover:bg-orange-50 disabled:opacity-50"
                                >
                                    {t('approval:return')}
                                </button>
                                <button
                                    onClick={() => handleAction('reject')}
                                    disabled={approveFormMutation.isPending}
                                    className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
                                >
                                    {t('approval:reject')}
                                </button>
                                <button
                                    onClick={() => handleAction('approve')}
                                    disabled={approveFormMutation.isPending}
                                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                                >
                                    {approveFormMutation.isPending ? t('approval:processing') : t('approval:approve')}
                                </button>
                            </div>
                        </div>
                    )}

                    {!isApprover && form.status === 'pending' && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-yellow-700">
                            {t('approval:notApprover')}
                        </div>
                    )}
                </div>

                {/* 右側邊欄 - 審批流程 */}
                <div className="lg:col-span-1 space-y-4">
                    {/* 審批流程 */}
                    <div className="bg-white rounded-lg shadow p-4">
                        <h3 className="font-medium text-gray-900 mb-3">{t('approval:approvalFlow')}</h3>
                        <div className="space-y-2">
                            {approvalFlow.map((step, idx) => {
                                const isSkipped = step.status === 'skipped' || step.isSkipped;
                                return (
                                    <div 
                                        key={step.stepId || idx}
                                        className={`p-3 rounded text-sm ${
                                            isSkipped ? 'bg-gray-100 border-l-4 border-gray-400 opacity-60' :
                                            step.status === 'current' ? 'bg-blue-50 border-l-4 border-blue-500' :
                                            step.status === 'completed' || step.status === 'approved' ? 'bg-green-50 border-l-4 border-green-500' :
                                            step.status === 'rejected' ? 'bg-red-50 border-l-4 border-red-500' :
                                            'bg-gray-50 border-l-4 border-gray-300'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className={`font-medium ${isSkipped ? 'line-through text-gray-500' : ''}`}>
                                                {getText(step.stepName)}
                                            </span>
                                            {isSkipped ? (
                                                <span className="text-xs text-gray-500">⏭ {t('approval:skipped') || '已跳過'}</span>
                                            ) : step.status === 'completed' || step.status === 'approved' ? (
                                                <span className="text-xs text-green-600">✓ {t('approval:approved')}</span>
                                            ) : step.status === 'rejected' ? (
                                                <span className="text-xs text-red-600">✗ {t('approval:rejected')}</span>
                                            ) : step.status === 'current' ? (
                                                <span className="text-xs text-blue-600">{t('approval:pending')}</span>
                                            ) : (
                                                <span className="text-xs text-gray-400">{t('approval:waiting')}</span>
                                            )}
                                        </div>
                                        {step.approvers?.length > 0 && !isSkipped && (
                                            <div className="mt-1 text-xs text-gray-600">
                                                {step.approvers.map(a => a.displayName).join(', ')}
                                            </div>
                                        )}
                                        {step.comment && (
                                            <div className="mt-2 p-2 bg-white rounded text-xs text-gray-600 border">
                                                <span className="font-medium">{t('approval:opinion')}：</span>{step.comment}
                                            </div>
                                        )}
                                        {step.actionAt && (
                                            <div className="mt-1 text-xs text-gray-400">
                                                {new Date(step.actionAt).toLocaleString('zh-TW')}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* 審批歷史 */}
                    {form.approvalHistory && form.approvalHistory.length > 0 && (
                        <div className="bg-white rounded-lg shadow p-4">
                            <h3 className="font-medium text-gray-900 mb-3">{t('approval:approvalHistory')}</h3>
                            <div className="space-y-3">
                                {form.approvalHistory.map((history, idx) => (
                                    <div 
                                        key={idx}
                                        className={`p-3 rounded text-sm border-l-4 ${
                                            history.action === 'approve' ? 'border-green-500 bg-green-50' :
                                            history.action === 'reject' ? 'border-red-500 bg-red-50' :
                                            history.action === 'return' ? 'border-orange-500 bg-orange-50' :
                                            history.action === 'submit' ? 'border-blue-500 bg-blue-50' :
                                            'border-gray-300 bg-gray-50'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between">
                                            <span className="font-medium">
                                                {history.actionBy?.displayName}
                                            </span>
                                            <span className={`text-xs px-2 py-0.5 rounded ${
                                                history.action === 'approve' ? 'bg-green-100 text-green-700' :
                                                history.action === 'reject' ? 'bg-red-100 text-red-700' :
                                                history.action === 'return' ? 'bg-orange-100 text-orange-700' :
                                                history.action === 'submit' ? 'bg-blue-100 text-blue-700' :
                                                'bg-gray-100 text-gray-700'
                                            }`}>
                                                {t(`approval:action.${history.action}`, history.action)}
                                            </span>
                                        </div>
                                        {/* 顯示流程步驟名稱 */}
                                        {history.stepName && (
                                            <div className="text-xs text-gray-500 mt-1">
                                                📍 {getText(history.stepName)}
                                            </div>
                                        )}
                                        {history.actionBy?.jobTitle && (
                                            <div className="text-xs text-gray-500 mt-1">
                                                {history.actionBy.department} • {history.actionBy.jobTitle}
                                            </div>
                                        )}
                                        {history.comment && (
                                            <div className="mt-2 p-2 bg-white rounded text-xs text-gray-700 border">
                                                "{history.comment}"
                                            </div>
                                        )}
                                        <div className="text-xs text-gray-400 mt-2">
                                            {new Date(history.timestamp).toLocaleString('zh-TW')}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* 拒絕/退回確認彈窗 */}
            {showRejectModal && (
                <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                        <h3 className="text-lg font-medium mb-4">
                            {pendingAction === 'reject' ? t('approval:modal.rejectTitle') : t('approval:modal.returnTitle')}
                        </h3>
                        <p className="text-sm text-gray-500 mb-4">
                            {pendingAction === 'reject' ? t('approval:modal.pleaseInputRejectReason') : t('approval:modal.pleaseInputReturnReason')}
                        </p>
                        <textarea
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                            placeholder={t('approval:modal.inputReasonPlaceholder')}
                            className="w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                            rows={4}
                            autoFocus
                        />
                        <div className="mt-4 flex justify-end gap-3">
                            <button
                                onClick={() => setShowRejectModal(false)}
                                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                {t('approval:modal.cancel')}
                            </button>
                            <button
                                onClick={confirmReject}
                                className={`px-4 py-2 rounded-lg text-white ${
                                    pendingAction === 'reject' 
                                        ? 'bg-red-600 hover:bg-red-700' 
                                        : 'bg-orange-600 hover:bg-orange-700'
                                }`}
                            >
                                {t('approval:modal.confirm')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FormApproval;
